// ── Adapter HTTP do IBluehubGateway ───────────────────────────────
// Implementa a porta contra /api/bluehub, a rota do NOSSO servidor
// (server/bluehub/). É ele quem guarda a X-API-Key do Hub, lida do env —
// o navegador nunca a vê, e por isso não existe modo "direto" aqui.
//
// A sessão do Supabase vai como Bearer: é dela que o servidor tira o
// X-Actor-Id da operação, que responde "quem no CRM fez isto".

import { getSupabase } from '@infra/supabase/client';
import type {
  IBluehubGateway, BluehubAcao, BluehubFalha, BluehubIdempotencia,
  BluehubCodigoErro, BluehubDicaConflito,
} from '@modules/bluehub';
import type {
  BluehubNovaEmpresa, BluehubCorrecaoEmpresa, BluehubEmpresaCriada,
  BluehubEmpresaAtualizada, BluehubPlanos, BluehubItemVidas,
  BluehubVidasProvisionadas, BluehubPessoaConvidada, BluehubConvites,
  BluehubCobranca, BluehubContrato, BluehubAcesso, BluehubSincronizacaoEmpresas,
} from '@modules/bluehub';

const ROTA = '/api/bluehub';

/** Erro que a UI recebe. Carrega a régua de retry inteira: é ela que decide
 *  se o botão "tentar de novo" pode existir, e com qual chave. */
export class BluehubGatewayError extends Error {
  readonly falha: BluehubFalha;

  constructor(falha: BluehubFalha) {
    super(falha.mensagem);
    this.name = 'BluehubGatewayError';
    this.falha = falha;
  }
}

interface RespostaOk<T> {
  ok: true;
  acao: BluehubAcao;
  status: number;
  data: T;
  idempotencyKey: string | null;
}

interface RespostaErro {
  origem?: 'hub' | 'crm';
  error?: string;
  codigo?: BluehubCodigoErro;
  dica?: BluehubDicaConflito | null;
  detalhes?: BluehubFalha['detalhes'];
  problemas?: string[];
  retryAfterSegundos?: number | null;
  repetirComMesmaChave?: boolean;
  repetirComChaveNova?: boolean;
  // O servidor manda estes três como null quando ausentes.
  code?: string | null;
  retryable?: boolean | null;
  correlationId?: string | null;
}

export const falhaDe = (status: number, corpo: RespostaErro): BluehubFalha => ({
  ...(typeof corpo.code === 'string' ? { code: corpo.code } : {}),
  ...(typeof corpo.retryable === 'boolean' ? { retryable: corpo.retryable } : {}),
  ...(typeof corpo.correlationId === 'string' ? { correlationId: corpo.correlationId } : {}),
  origem: corpo.origem ?? 'crm',
  mensagem: corpo.error || corpo.problemas?.join(' · ') || `Falha ao falar com o Hub (HTTP ${status}).`,
  codigo: corpo.codigo ?? 'desconhecido',
  status,
  dica: corpo.dica ?? null,
  detalhes: corpo.detalhes ?? (corpo.problemas
    ? corpo.problemas.map(p => ({ tipo: null, campo: null, mensagem: p }))
    : null),
  retryAfterSegundos: corpo.retryAfterSegundos ?? null,
  repetirComMesmaChave: corpo.repetirComMesmaChave ?? false,
  repetirComChaveNova: corpo.repetirComChaveNova ?? false,
});

export class BluehubApiClient implements IBluehubGateway {
  private async chamar<T>(
    acao: BluehubAcao,
    params: Record<string, unknown>,
    idempotencia?: BluehubIdempotencia,
  ): Promise<T> {
    const client = getSupabase();
    if (!client) throw new Error('Supabase não inicializado.');
    const { data } = await client.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Sessão inválida.');

    const response = await fetch(ROTA, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ acao, params, idempotencia }),
    });

    // Ler como texto antes de parsear: com o backend fora do ar quem responde
    // é o proxy, em texto puro, e um .json() direto esconderia o motivo.
    const texto = await response.text();
    let corpo: RespostaOk<T> & RespostaErro;
    try {
      corpo = JSON.parse(texto);
    } catch {
      const trecho = texto.trim().slice(0, 160) || '(resposta vazia)';
      throw new Error(`HTTP ${response.status} de ${ROTA} — ${trecho}`);
    }

    if (!response.ok) {
      const correlationId = corpo.correlationId || response.headers.get('x-correlation-id') || undefined;
      throw new BluehubGatewayError(falhaDe(response.status, { ...corpo, correlationId }));
    }
    return corpo.data;
  }

  criarEmpresa = (input: {
    empresaId: string; empresa: BluehubNovaEmpresa; idempotencia: BluehubIdempotencia;
  }) =>
    this.chamar<BluehubEmpresaCriada>(
      'criar-empresa',
      { empresaId: input.empresaId, empresa: input.empresa },
      input.idempotencia,
    );

  atualizarEmpresa = (input: {
    empresaId: string; empresaPublicId: string;
    campos: BluehubCorrecaoEmpresa; idempotencia: BluehubIdempotencia;
  }) =>
    this.chamar<BluehubEmpresaAtualizada>(
      'atualizar-empresa',
      { empresaId: input.empresaId, empresaPublicId: input.empresaPublicId, campos: input.campos },
      input.idempotencia,
    );

  listarPlanos = (input: { empresaId: string; empresaPublicId: string }) =>
    this.chamar<BluehubPlanos>('listar-planos', input);

  provisionarVidas = (input: {
    empresaId: string; empresaPublicId: string; vidas: BluehubItemVidas[];
    encerrarProspeccao?: boolean; idempotencia: BluehubIdempotencia;
  }) =>
    this.chamar<BluehubVidasProvisionadas>(
      'provisionar-vidas',
      {
        empresaId: input.empresaId,
        empresaPublicId: input.empresaPublicId,
        vidas: input.vidas,
        encerrarProspeccao: input.encerrarProspeccao ?? false,
      },
      input.idempotencia,
    );

  convidarVidas = (input: {
    empresaId: string; empresaPublicId: string;
    vidas: BluehubPessoaConvidada[]; idempotencia: BluehubIdempotencia;
  }) =>
    this.chamar<BluehubConvites>(
      'convidar-vidas',
      { empresaId: input.empresaId, empresaPublicId: input.empresaPublicId, vidas: input.vidas },
      input.idempotencia,
    );

  emitirCobranca = (input: {
    empresaId: string; empresaPublicId: string; idempotencia: BluehubIdempotencia;
  }) =>
    this.chamar<BluehubCobranca>(
      'emitir-cobranca',
      { empresaId: input.empresaId, empresaPublicId: input.empresaPublicId },
      input.idempotencia,
    );

  enviarContrato = (input: {
    empresaId: string; empresaPublicId: string; idempotencia: BluehubIdempotencia;
  }) =>
    this.chamar<BluehubContrato>(
      'enviar-contrato',
      { empresaId: input.empresaId, empresaPublicId: input.empresaPublicId },
      input.idempotencia,
    );

  solicitarAcesso = (input: {
    empresaId: string; empresaPublicId: string; email: string; idempotencia: BluehubIdempotencia;
  }) =>
    this.chamar<BluehubAcesso>(
      'solicitar-acesso',
      { empresaId: input.empresaId, empresaPublicId: input.empresaPublicId, email: input.email },
      input.idempotencia,
    );

  lerVidasDaEmpresa = (input: { empresaId: string }) =>
    this.chamar<unknown>('ler-vidas', input);

  lerCobrancasDaEmpresa = (input: { empresaId: string }) =>
    this.chamar<unknown>('ler-cobrancas', input);

  sincronizarEmpresas = () =>
    this.chamar<BluehubSincronizacaoEmpresas>('sincronizar-empresas', {});
}
